import React, { useContext, useEffect, useState } from "react";

import { SalesContext } from "../../context/salesContext";
import { StatCard } from "../ui";

const fmt = (n) =>
    new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD", maximumFractionDigits: 0 }).format(n);

const SalesStats = () => {
    const { action } = useContext(SalesContext);
    const [stats, setStats] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            const data = await action.getFinancialStats();
            if (cancelled || !data || typeof data !== "object" || Array.isArray(data)) return;
            setStats({
                outstandingBalance: data.outstandingBalance || 0,
                outstandingCount: data.outstandingCount || 0,
                thisMonthRevenue: data.thisMonthRevenue || 0,
                thisMonthProfit: data.thisMonthProfit || 0,
                thisMonthCount: data.thisMonthCount || 0,
                lastMonthRevenue: data.lastMonthRevenue || 0,
                lastMonthProfit: data.lastMonthProfit || 0,
                lastMonthCount: data.lastMonthCount || 0,
            });
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const revenueHint = () => {
        if (!stats) return null;
        const diff = stats.thisMonthRevenue - stats.lastMonthRevenue;
        if (diff === 0) return `Same as last month (${fmt(stats.lastMonthRevenue)})`;
        const sign = diff > 0 ? "↑" : "↓";
        return `${sign} ${fmt(Math.abs(diff))} vs last month`;
    };

    const profitHint = () => {
        if (!stats) return null;
        const diff = stats.thisMonthProfit - stats.lastMonthProfit;
        if (diff === 0) return `Same as last month (${fmt(stats.lastMonthProfit)})`;
        const sign = diff > 0 ? "↑" : "↓";
        return `${sign} ${fmt(Math.abs(diff))} vs last month`;
    };

    const countHint = () => {
        if (!stats) return null;
        const diff = stats.thisMonthCount - stats.lastMonthCount;
        if (diff === 0) return `Same as last month (${stats.lastMonthCount})`;
        const sign = diff > 0 ? "↑" : "↓";
        return `${sign} ${Math.abs(diff)} vs last month`;
    };

    return (
        <section
            aria-label="Sales summary"
            className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4"
        >
            <StatCard
                tone="warning"
                label="Orders This Month"
                value={stats ? stats.thisMonthCount : "—"}
                icon={<i className="fas fa-receipt" />}
                hint={countHint()}
            />
            <StatCard
                tone="brand"
                label="Revenue This Month"
                value={stats ? fmt(stats.thisMonthRevenue) : "—"}
                icon={<i className="fas fa-chart-line" />}
                hint={revenueHint()}
            />
            <StatCard
                tone="success"
                label="Profit This Month"
                value={stats ? fmt(stats.thisMonthProfit) : "—"}
                icon={<i className="fas fa-coins" />}
                hint={profitHint()}
            />
            <StatCard
                tone="danger"
                label="Outstanding Balance"
                value={stats ? fmt(stats.outstandingBalance) : "—"}
                icon={<i className="fas fa-dollar-sign" />}
                hint={stats ? `Across ${stats.outstandingCount} order${stats.outstandingCount === 1 ? "" : "s"}` : null}
            />
        </section>
    );
};

export default SalesStats;
